import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FileCheck, TrendingUp, Brain, Target, Shield, Zap, ArrowRight, Sparkles, Globe, Send, Languages, Mic, Plane, Wrench, Home, Clock, ClipboardCheck, Users, GraduationCap, Award } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import LoginModal from '../components/LoginModal';
import SEO, { faqSchema } from '../components/SEO';
import { SERVICES, FAQ } from '../data/siteContent';
import './HowItWorksPage.css';

const HowItWorksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  return (
    <div className="hiw">
      <SEO
        title="Nasıl Çalışır?"
        description="isBulsun.com yapay zeka ile nasıl çalışır? Akıllı ilan eşleştirme, form taslağı hazırlama, CV optimizasyonu, kariyer gelişimi ve yurtdışı remote iş ilanları."
        path="/nasil-calisir"
        structuredData={faqSchema(FAQ)}
      />
      <section className="hiw__hero">
        <h1 className="hiw__title">
          Kariyer Asistanın <span className="hiw__title-accent">İş Arama Sürecini</span> Otomatikleştirsin
        </h1>
        <p className="hiw__subtitle">
          Geleneksel iş arama yöntemlerini unutun. isBulsun.com yapay zeka ile en uygun iş fırsatlarını sizin için yakalar, profilinize göre eşleştirir, CV'nizi optimize eder ve başvuru süreçlerinizi kat kat hızlandırır.
        </p>
      </section>

      <section className="hiw__steps">
        <div className="hiw__steps-container">

          <div className="hiw__step">
            <div className="hiw__step-number">01</div>
            <div className="hiw__step-icon">
              <Search size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Akıllı Eşleştirme</h2>
              <p className="hiw__step-desc">
                CV'nizi yükleyin ve ne tür bir iş aradığınızı doğal dille anlatın. Yapay zekamız CV'nizdeki deneyimlerinizi, yeteneklerinizi ve beklentilerinizi analiz ederek size en uygun ilanları bulur.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Sadece size uygun ilanları görürsünüz, binlerce alakasız ilan arasında kaybolmazsınız</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>Yapay zeka tecrübenize, lokasyonunuza ve beklentilerinize göre eşleştirme yapar</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">02</div>
            <div className="hiw__step-icon hiw__step-icon--green">
              <Send size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Form Taslağı Hazırlama</h2>
              <p className="hiw__step-desc">
                Siz gece uyurken yapay zeka arka planda çalışır: Profilinizle eşleşen fırsatları yakalar, CV'nizi ilanın gereksinimlerine göre optimize eder ve başvuru formunuzu hazır tutar. Size sadece sabah bildirimi kontrol edip tek tıkla onay vermek kalır.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>Uygun ilanlar bulunduğunda anında bildirim alırsınız, zaman kaybetmezsiniz</span>
                </div>
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>Formların kontrolü her zaman sizdedir; onaylamadığınız hiçbir form gönderilmez</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">03</div>
            <div className="hiw__step-icon hiw__step-icon--purple">
              <FileCheck size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">İlana Özel CV Optimizasyonu</h2>
              <p className="hiw__step-desc">
                Yapay zeka her ilan için CV'nizi özel olarak optimize eder. İlanın gerektirdiği anahtar kelimeleri, yetkinlikleri ve formatı analiz ederek CV'nizi o ilana en uygun hale getirir. ATS sistemlerinden geçme oranınız katlanarak artar.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Her başvuru için ilanın gereksinimlerine göre özelleştirilmiş CV hazırlanır</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>ATS uyumlu format ve anahtar kelime optimizasyonu ile öne çıkarsınız</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">04</div>
            <div className="hiw__step-icon hiw__step-icon--orange">
              <TrendingUp size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Seviye Ölçümü ve Kariyer Gelişimi</h2>
              <p className="hiw__step-desc">
                Yapay zeka yetkinliklerinizi analiz ederek sektördeki seviyenizi ölçer. Gelişim alanlarınızı belirler, sektör trendlerini takip eder ve kariyerinize yön vermeniz için kişiselleştirilmiş öneriler sunar.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>Sektörünüzdeki seviyenizi ve piyasa değerinizi öğrenin</span>
                </div>
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>Hangi yetkinlikleri geliştirmeniz gerektiğini ve hangi sertifikaların değer katacağını bilin</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">05</div>
            <div className="hiw__step-icon hiw__step-icon--blue">
              <Globe size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Yurtdışı Remote İlanlar</h2>
              <p className="hiw__step-desc">
                Yurtdışındaki remote iş ilanları da listelenir ve eşleştirme sürecine dahil edilir. Yabancı dil bilmenize gerek yok: HireThink Yapay Zeka Teknolojileri'nin geliştirdiği toplantı çevirici ile dil engeli tamamen ortadan kalkar.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Languages size={16} />
                  <span>Karşı tarafın konuşması anlık olarak Türkçe yazıya çevrilir</span>
                </div>
                <div className="hiw__highlight">
                  <Mic size={16} />
                  <span>Siz Türkçe konuşursunuz, yapay zeka konuşmanızı karşı tarafa İngilizce olarak sesli iletir</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__divider">
            <div className="hiw__divider-line"></div>
            <div className="hiw__divider-badge">
              <Plane size={16} />
              <span>ABD &amp; Kanada Fırsatları</span>
            </div>
            <div className="hiw__divider-line"></div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">06</div>
            <div className="hiw__step-icon hiw__step-icon--red">
              <Globe size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">ABD ve Kanada'da Mavi Yaka İlanları</h2>
              <p className="hiw__step-desc">
                Amerika ve Kanada'da mavi yaka mesleklerinde işgücü talebi yüksek: Tır şoförü, aşçı, garson, oto tamircisi, tesisatçı, boyacı, elektrikçi, kaynakçı gibi alanlarda çok sayıda açık pozisyon ilan ediliyor. isBulsun yapay zekası bu ilanları sizin için tarar ve profilinizle eşleştirir; başvuru değerlendirmesi ve işe alım kararı ilgili işverene aittir.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>ABD ve Kanada'daki mavi yaka iş ilanları sürekli güncellenir</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>Mesleğinize ve deneyiminize göre en uygun pozisyonlar otomatik eşleştirilir</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">07</div>
            <div className="hiw__step-icon hiw__step-icon--orange">
              <Clock size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Geçici Çalışma Programları Hakkında Bilgilendirme</h2>
              <p className="hiw__step-desc">
                Sistem, ABD ve Kanada'daki resmi geçici çalışma programları (H-2B, TFWP) hakkında bilgilendirme sunar ve gerekli evrak listeleri için rehberlik taslakları hazırlar.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>H-2B ve TFWP gibi programların şartları ve süreçleri hakkında güncel bilgilendirme sunulur</span>
                </div>
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>İşveren sponsorluğu ve masraf paylaşımı konularında genel bilgi verilir; nihai koşullar işverene ve resmî makamlara bağlıdır</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">08</div>
            <div className="hiw__step-icon hiw__step-icon--green">
              <Home size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Kalıcı Oturum Yolları Hakkında Bilgilendirme</h2>
              <p className="hiw__step-desc">
                Sistem, Kanada'nın Express Entry ve Provincial Nominee programları ile ABD'nin EB-3 vizesi gibi kalıcı oturum yolları hakkında bilgilendirme sunar; uygunluk değerlendirmesi ve gerekli evrak listeleri için rehberlik taslakları hazırlar. Nihai değerlendirme ve karar resmî makamlara aittir.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Kanada'da 2 yıllık çalışma deneyiminin kalıcı oturum başvurusunda nasıl değerlendirildiği hakkında bilgi verilir</span>
                </div>
                <div className="hiw__highlight">
                  <Users size={16} />
                  <span>Aile bireyleri için oturum imkanları hakkında da genel bilgilendirme sunulur</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">09</div>
            <div className="hiw__step-icon hiw__step-icon--purple">
              <Wrench size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">En Çok Aranan Mavi Yaka Meslekler</h2>
              <p className="hiw__step-desc">
                Kuzey Amerika'da ciddi işgücü açığı olan meslekler: Uzun yol tır şoförü (CDL), aşçı ve şef, garson, oto tamircisi ve oto boyacı, sıhhi tesisatçı, boya badana ustası, elektrikçi, kaynakçı, inşaat işçisi, çiftlik işçisi, et kesim uzmanı ve paketleme operatörü. Bu mesleklerde deneyimli profesyoneller için ücret seviyeleri genellikle yüksektir.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <TrendingUp size={16} />
                  <span>ABD'de tır şoförü yıllık ortalama 55.000–80.000 USD, Kanada'da 50.000–70.000 CAD kazanır</span>
                </div>
                <div className="hiw__highlight">
                  <Zap size={16} />
                  <span>Aşçı, tesisatçı, elektrikçi gibi mesleklerde kronik işgücü açığı devam ediyor</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">10</div>
            <div className="hiw__step-icon hiw__step-icon--blue">
              <ClipboardCheck size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Vize ve Başvuru Süreci Rehberliği</h2>
              <p className="hiw__step-desc">
                Yapay zeka, vize süreciniz için gerekli belgeleri listeler, başvuru formu taslaklarını hazırlamanıza yardımcı olur ve süreç boyunca sizi bilgilendirir. LMIA (Kanada) veya Labor Certification (ABD) süreçleri hakkında genel, adım adım rehberlik sunulur; nihai onay resmî makamlara aittir.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Shield size={16} />
                  <span>Gerekli belge listeleri ve başvuru formu taslakları yapay zeka tarafından hazırlanır</span>
                </div>
                <div className="hiw__highlight">
                  <Brain size={16} />
                  <span>Süreç durumunuz takip edilir, eksik belge olursa bilgilendirilirsiniz</span>
                </div>
              </div>
            </div>
          </div>

          <div className="hiw__step">
            <div className="hiw__step-number">11</div>
            <div className="hiw__step-icon hiw__step-icon--red">
              <GraduationCap size={28} />
            </div>
            <div className="hiw__step-content">
              <h2 className="hiw__step-title">Eğitim ve Sertifika Rehberliği</h2>
              <p className="hiw__step-desc">
                ABD ve Kanada'da birçok mavi yaka meslek için yerel sertifika ve lisans gereklidir. CDL (ticari ehliyet), Red Seal (Kanada zanaat sertifikası), OSHA güvenlik eğitimi, Food Handler sertifikası gibi belgeler hakkında size bilgilendirme ve rehberlik sunuyoruz. Bu sertifikaların çalışma izni ve kalıcı oturum başvurularında nasıl değerlendirildiğine dair genel bilgi verilir; nihai etki resmî makamların kriterlerine bağlıdır.
              </p>
              <div className="hiw__step-highlights">
                <div className="hiw__highlight">
                  <Award size={16} />
                  <span>Mesleğinize uygun sertifika programları hakkında bilgi verilir: CDL, Red Seal, OSHA, WHMIS, Food Handler vb.</span>
                </div>
                <div className="hiw__highlight">
                  <Target size={16} />
                  <span>Sertifikaların kalıcı oturum puanlamasındaki genel etkisi hakkında bilgilendirme sunulur</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="hiw__extra">
        <h2 className="hiw__extra-title">Sunduğumuz hizmetler</h2>
        <div className="hiw__services">
          {SERVICES.map((srv, i) => (
            <article key={i} className="hiw__service">
              <h3 className="hiw__service-title">{srv.name}</h3>
              <p className="hiw__service-desc">{srv.description}</p>
            </article>
          ))}
        </div>

        <h2 className="hiw__extra-title hiw__extra-title--faq">Sık sorulan sorular</h2>
        <div className="hiw__faq">
          {FAQ.map((item, i) => (
            <details key={i} className="hiw__faq-item">
              <summary>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="hiw__cta">
        <div className="hiw__cta-badge">
          <Sparkles size={14} />
          <span>Tamamen Ücretsiz</span>
        </div>
        <h2 className="hiw__cta-title">Yapay Zeka ile Başvuru Süreçlerini 10 Kat Hızlandırın</h2>
        <p className="hiw__cta-desc">
          Yapay zekanın gücüyle kariyerinizi bir üst seviyeye taşıyın. Hemen ilan taramaya başlayın veya üye olarak tüm özelliklerin kilidini açın.
        </p>
        <div className="hiw__cta-actions">
          {user ? (
            <button className="hiw__cta-btn hiw__cta-btn--primary" onClick={() => navigate('/panel')}>
              <ArrowRight size={16} />
              Kariyerime Git
            </button>
          ) : (
            <>
              <button className="hiw__cta-btn hiw__cta-btn--primary" onClick={() => navigate('/')}>
                <Search size={16} />
                AI Asistanı Deneyin
              </button>
              <button className="hiw__cta-btn hiw__cta-btn--secondary" onClick={() => setIsLoginOpen(true)}>
                Profilinizi Eşleştirin
                <ArrowRight size={16} />
              </button>
            </>
          )}
        </div>
        <div className="hiw__cta-features">
          <span>Akıllı Eşleştirme</span>
          <span className="hiw__cta-dot"></span>
          <span>Form Taslağı Hazırlama</span>
          <span className="hiw__cta-dot"></span>
          <span>CV Optimizasyonu</span>
          <span className="hiw__cta-dot"></span>
          <span>Yurtdışı Remote</span>
          <span className="hiw__cta-dot"></span>
          <span>ABD &amp; Kanada İş İmkanı</span>
        </div>
      </section>

      <LoginModal isOpen={isLoginOpen} onClose={() => setIsLoginOpen(false)} />
    </div>
  );
};

export default HowItWorksPage;
